import { useState } from 'react';
import type {
  AddressComponent,
  AddressComponentConfig,
  AddressConfig,
  ConditionalVisibility,
  FieldDef,
  RepeaterSubField,
  RepeaterSubFieldType,
} from '@jfh/intake-shared';
import {
  ADDRESS_COMPONENT_LABELS,
  offersPostcodeLookup,
  resolveAddressComponents,
  resolveRepeaterSubFields,
  resolveStaticOptions,
} from '@jfh/intake-shared';
import { Icon } from '../../components/Icon.js';
import { countrySources, subDefaultSources } from './logic.js';
import { MiniButton, SectionTitle, ToggleRow } from './ui.js';

// ---------------------------------------------------------------------------
// Address lines
// ---------------------------------------------------------------------------

function patchComponent(
  address: AddressConfig | undefined,
  key: AddressComponent,
  patch: Partial<AddressComponentConfig>,
): AddressConfig {
  return {
    ...address,
    components: resolveAddressComponents({ address }).map((c) => (c.key === key ? { ...c, ...patch } : c)),
  };
}

/**
 * "Which lines to ask for": ask for / required per line, with label overrides.
 * With `fields`, a Country line that's asked for can start as another
 * question's answer.
 */
export function AddressLines({
  address,
  onChange,
  note,
  fields,
}: {
  address: AddressConfig | undefined;
  onChange: (next: AddressConfig) => void;
  note?: string;
  fields?: FieldDef[];
}) {
  const asksCountry = resolveAddressComponents({ address }).some((c) => c.key === 'country' && c.enabled);
  const sources = fields ? countrySources(fields) : [];
  const from = address?.countryFrom ?? '';
  const fromMissing = Boolean(from) && !sources.some((x) => x.id === from);
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 56px 64px',
            gap: 6,
            padding: '7px 12px',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-3)',
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span>Line</span>
          <span style={{ textAlign: 'center' }}>Ask for</span>
          <span style={{ textAlign: 'center' }}>Required</span>
        </div>
        {resolveAddressComponents({ address }).map((c) => (
          <div
            key={c.key}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 56px 64px',
              gap: 6,
              alignItems: 'center',
              padding: '5px 12px',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <input
              value={c.label ?? ''}
              placeholder={ADDRESS_COMPONENT_LABELS[c.key]}
              aria-label={`Label for ${ADDRESS_COMPONENT_LABELS[c.key]}`}
              onChange={(e) => onChange(patchComponent(address, c.key, { label: e.target.value || undefined }))}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: 13,
                color: 'var(--text-1)',
                outline: 'none',
                minWidth: 0,
              }}
            />
            <span style={{ display: 'flex', justifyContent: 'center' }}>
              <input
                type="checkbox"
                checked={c.enabled}
                aria-label={`Ask for ${ADDRESS_COMPONENT_LABELS[c.key]}`}
                onChange={() =>
                  onChange(
                    patchComponent(address, c.key, { enabled: !c.enabled, required: c.enabled ? false : c.required }),
                  )
                }
                style={{ width: 16, height: 16, accentColor: 'var(--accent)' }}
              />
            </span>
            <span style={{ display: 'flex', justifyContent: 'center' }}>
              <input
                type="checkbox"
                checked={c.required}
                disabled={!c.enabled}
                aria-label={`${ADDRESS_COMPONENT_LABELS[c.key]} is required`}
                onChange={() => onChange(patchComponent(address, c.key, { required: !c.required }))}
                style={{ width: 16, height: 16, accentColor: 'var(--accent)' }}
              />
            </span>
          </div>
        ))}
      </div>
      <div className="field-help" style={{ marginTop: 0 }}>
        Tick Country for overseas suppliers.{note ? ` ${note}` : ''}
      </div>
      {resolveAddressComponents({ address }).some((c) => c.key === 'postcode' && c.enabled) && (
        <ToggleRow
          boxed
          title="Find by postcode"
          hint={
            offersPostcodeLookup(address)
              ? 'Applicants type a UK postcode and pick their address (Ideal Postcodes), or type it themselves.'
              : 'Applicants type every line themselves.'
          }
          on={offersPostcodeLookup(address)}
          onToggle={() => {
            const next: AddressConfig = { ...address, components: resolveAddressComponents({ address }) };
            if (offersPostcodeLookup(address)) next.postcodeLookup = false;
            else delete next.postcodeLookup;
            onChange(next);
          }}
        />
      )}
      {fields && asksCountry && (
        <div>
          <label className="field-label">Start the country as the answer to</label>
          <select
            className="select"
            value={from}
            onChange={(e) => {
              const next: AddressConfig = { ...address, components: resolveAddressComponents({ address }) };
              if (e.target.value) next.countryFrom = e.target.value;
              else delete next.countryFrom;
              onChange(next);
            }}
          >
            <option value="">Nothing (applicants choose)</option>
            {fromMissing && <option value={from}>A question you deleted</option>}
            {sources.map((x) => (
              <option key={x.id} value={x.id}>
                {x.label || 'Untitled question'}
                {x.iqCountries ? '' : ' (not the iQ country list)'}
              </option>
            ))}
          </select>
          <div className="field-help">
            {from
              ? 'Filled in once that question is answered. Applicants can still choose another country here.'
              : 'Pick a dropdown that uses the iQ country list to save applicants choosing twice.'}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contacts / repeating group
// ---------------------------------------------------------------------------

const SUB_TYPES: [RepeaterSubFieldType, string][] = [
  ['short_text', 'Short answer'],
  ['long_text', 'Long answer'],
  ['email', 'Email address'],
  ['phone', 'Phone number'],
  ['number', 'Number'],
  ['date', 'Date'],
  ['select', 'Dropdown list'],
  ['address', 'Address'],
];

/** Each contact / entry: its name, how many, and its sub-questions. */
export function RepeatSection({
  field: f,
  fields,
  update,
}: {
  field: FieldDef;
  fields: FieldDef[];
  update: (patch: Partial<FieldDef>) => void;
}) {
  const subs = resolveRepeaterSubFields(f);
  const [openId, setOpenId] = useState<string | null>(null);
  const setSubs = (next: RepeaterSubField[]) => update({ subFields: next });
  const setSub = (id: string, patch: Partial<RepeaterSubField>) =>
    setSubs(subs.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= subs.length) return;
    const next = [...subs];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setSubs(next);
  };

  return (
    <section style={{ display: 'grid', gap: 10 }}>
      <SectionTitle>{f.type === 'contact' ? 'Each contact asks for' : 'Each entry asks for'}</SectionTitle>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: 'var(--text-2)' }}>
        <span>Each one is called</span>
        <input
          className="input"
          style={{ width: 130, height: 32 }}
          value={f.itemLabel ?? ''}
          placeholder={f.type === 'contact' ? 'Contact' : 'Entry'}
          aria-label="Name for one entry"
          onChange={(e) => update({ itemLabel: e.target.value || undefined })}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: 'var(--text-2)' }}>
        <span>Ask for at least</span>
        <input
          className="input"
          type="number"
          min={0}
          style={{ width: 64, height: 32 }}
          value={f.minItems ?? ''}
          placeholder="0"
          aria-label="At least"
          onChange={(e) => update({ minItems: e.target.value === '' ? undefined : Number(e.target.value) })}
        />
        <span>and at most</span>
        <input
          className="input"
          type="number"
          min={1}
          style={{ width: 64, height: 32 }}
          value={f.maxItems ?? ''}
          placeholder="any"
          aria-label="At most"
          onChange={(e) => update({ maxItems: e.target.value === '' ? undefined : Number(e.target.value) })}
        />
      </div>

      <div style={{ display: 'grid', gap: 6 }}>
        {subs.map((s, i) => {
          const open = openId === s.id;
          return (
            <div
              key={s.id}
              style={{
                padding: '6px 8px',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
              }}
            >
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input
                  value={s.label}
                  aria-label="Sub-question wording"
                  onChange={(e) => setSub(s.id, { label: e.target.value })}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 'none',
                    background: 'transparent',
                    fontSize: 13,
                    color: 'var(--text-1)',
                    outline: 'none',
                  }}
                />
                {s.type !== 'address' && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--text-3)' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(s.required)}
                      onChange={() => setSub(s.id, { required: s.required ? undefined : true })}
                      style={{ accentColor: 'var(--accent)' }}
                    />
                    Required
                  </label>
                )}
                <MiniButton label={open ? 'Hide details' : 'Show details'} onClick={() => setOpenId(open ? null : s.id)}>
                  <Icon name="settings" size={12} />
                </MiniButton>
                <MiniButton label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                  ↑
                </MiniButton>
                <MiniButton label="Remove" onClick={() => setSubs(subs.filter((x) => x.id !== s.id))}>
                  <Icon name="x" size={12} />
                </MiniButton>
              </div>
              {open && (
                <div style={{ display: 'grid', gap: 6, padding: '8px 2px 4px' }}>
                  <label className="field-label" style={{ margin: 0 }}>
                    Kind of answer
                  </label>
                  <select
                    className="select"
                    style={{ height: 32, fontSize: 12.5 }}
                    value={s.type}
                    onChange={(e) => setSub(s.id, { type: e.target.value as RepeaterSubFieldType })}
                  >
                    {SUB_TYPES.map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                  {s.type !== 'address' && (
                    <>
                      <label className="field-label" style={{ margin: 0 }}>
                        Start as the answer to
                      </label>
                      <select
                        className="select"
                        style={{ height: 32, fontSize: 12.5 }}
                        value={s.defaultFrom ?? ''}
                        aria-label="Start as the answer to"
                        onChange={(e) => setSub(s.id, { defaultFrom: e.target.value || undefined })}
                      >
                        <option value="">Nothing (applicants type it)</option>
                        {s.defaultFrom && !fields.some((x) => x.id === s.defaultFrom) && (
                          <option value={s.defaultFrom}>A question you deleted</option>
                        )}
                        {subDefaultSources(fields).map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.label || 'Untitled question'}
                          </option>
                        ))}
                      </select>
                      <div className="field-help" style={{ marginTop: 0 }}>
                        {s.defaultFrom
                          ? 'Each one starts with that answer (Companies House’s name, when it fills it in). Applicants can change it.'
                          : 'For example, a contact’s Company name can start as the company name.'}
                      </div>
                    </>
                  )}
                  {s.type === 'select' && (
                    <>
                      <label className="field-label" style={{ margin: 0 }}>
                        Answers (one per line)
                      </label>
                      <textarea
                        className="textarea"
                        rows={3}
                        value={(s.options ?? []).join('\n')}
                        onChange={(e) =>
                          setSub(s.id, {
                            options: e.target.value.split(/\n/).map((o) => o.trim()).filter(Boolean),
                          })
                        }
                      />
                    </>
                  )}
                  {s.type === 'address' && (
                    <AddressLines address={s.address} onChange={(address) => setSub(s.id, { address })} fields={fields} />
                  )}
                </div>
              )}
            </div>
          );
        })}
        <select
          className="select"
          style={{ height: 32, fontSize: 12.5 }}
          value=""
          aria-label="Add a sub-question"
          onChange={(e) => {
            if (!e.target.value) return;
            setSubs([
              ...subs,
              { id: crypto.randomUUID().slice(0, 8), type: e.target.value as RepeaterSubFieldType, label: 'New question' },
            ]);
          }}
        >
          <option value="">+ Add a sub-question…</option>
          {SUB_TYPES.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Companies House look-up
// ---------------------------------------------------------------------------

/** Answers of a question that mean "a limited company" (Ltd, PLC, LLP…). */
function limitedAnswers(f: FieldDef): string[] {
  return resolveStaticOptions(f)
    .map((o) => o.value)
    .filter((v) => /limited/i.test(v));
}

/** The company-number look-up card: what it fills, and the "only limited companies" rule. */
export function CompanyLookupSection({
  field: f,
  fields,
  update,
  onRuleAdded,
}: {
  field: FieldDef;
  fields: FieldDef[];
  update: (patch: Partial<FieldDef>) => void;
  onRuleAdded: (text: string) => void;
}) {
  const cfg = f.companyLookup ?? {};
  const set = (patch: Partial<NonNullable<FieldDef['companyLookup']>>) => {
    const next = { ...cfg, ...patch };
    if (!next.nameFieldId) delete next.nameFieldId;
    if (!next.addressFieldId) delete next.addressFieldId;
    update({ companyLookup: next.nameFieldId || next.addressFieldId ? next : undefined });
  };
  const hasRules = Boolean((f.visibleWhen as ConditionalVisibility | undefined)?.rules?.length);
  // Offer the "only for limited companies" rule when a company-type question exists.
  const typeQuestion = fields.find(
    (x) => x.id !== f.id && ['radio', 'select'].includes(x.type) && !x.optionsSource && limitedAnswers(x).length > 0,
  );

  return (
    <section
      style={{
        display: 'grid',
        gap: 10,
        padding: 14,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 13.5 }}>
        <Icon name="badge" size={15} /> Companies House look-up
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
        When the applicant enters a company number we check it with Companies House. We fill in and lock
        these two questions, and a company that isn’t active can’t apply.
      </div>
      <div>
        <label className="field-label">Fills in the company name</label>
        <select className="select" value={cfg.nameFieldId ?? ''} onChange={(e) => set({ nameFieldId: e.target.value })}>
          <option value="">Don’t fill in a name</option>
          {fields
            .filter((x) => x.type === 'short_text' && x.audience === 'customer')
            .map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
        </select>
      </div>
      <div>
        <label className="field-label">Fills in the registered address</label>
        <select
          className="select"
          value={cfg.addressFieldId ?? ''}
          onChange={(e) => set({ addressFieldId: e.target.value })}
        >
          <option value="">Don’t fill in an address</option>
          {fields
            .filter((x) => x.type === 'address' && x.audience === 'customer')
            .map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
        </select>
      </div>
      {!hasRules && typeQuestion && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            padding: '10px 12px',
            background: 'var(--accent-soft)',
            borderRadius: 'var(--r-md)',
            fontSize: 12.5,
          }}
        >
          <span style={{ flex: 1 }}>Only limited companies have a company number. Show this only for them?</span>
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              const answers = limitedAnswers(typeQuestion);
              update({
                visibleWhen: {
                  match: 'all',
                  rules: [
                    {
                      fieldId: typeQuestion.id,
                      op: answers.length > 1 ? 'in' : 'eq',
                      value: answers.join(','),
                    },
                  ],
                },
              });
              onRuleAdded(`Shown only when ${typeQuestion.label} is ${answers.join(' or ')}.`);
            }}
          >
            Yes, add the rule
          </button>
        </div>
      )}
      <div className="field-help" style={{ marginTop: 0 }}>
        Make it required (and shown only for limited companies) to make the check compulsory.
      </div>
    </section>
  );
}
